import { useEffect, useState } from "react";
import { CheckCircle, XCircle, PencilSimple, ShieldWarning } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { istToUtcIso, isAutoAccepted } from "../../lib/regularisation";

const COPY = {
    approve: { title: "Approve request", desc: "The requested outcome is applied to the day. A note is optional.", min: 0, icon: CheckCircle, cls: "bg-emerald-600 hover:bg-emerald-700", btn: "Approve" },
    modify: { title: "Approve with changes", desc: "Set the final attendance yourself. The employee sees the original request, your change and your reason.", min: 10, icon: PencilSimple, cls: "bg-indigo-600 hover:bg-indigo-700", btn: "Save decision" },
    reject: { title: "Reject request", desc: "The day keeps its recorded attendance. A reason of at least 10 characters is required.", min: 10, icon: XCircle, cls: "bg-rose-600 hover:bg-rose-700", btn: "Reject" },
    override: { title: "Super Admin override", desc: "Overrides any decision so far. Earlier corrections are rolled back and kept in history.", min: 10, icon: ShieldWarning, cls: "bg-slate-900 hover:bg-slate-800", btn: "Apply override" },
};

const REVIEW_COPY = {
    modify: { title: "Change accepted request", desc: "This request was accepted automatically. Set the final attendance yourself; the employee sees your change and your reason." },
    reject: { title: "Reject accepted request", desc: "The automatic acceptance is undone and the day goes back to its recorded attendance. A reason of at least 10 characters is required." },
};

function OutcomeFields({ v, setV, showOutcome }) {
    const needsChange = !showOutcome || v.outcome === "modify";
    return (
        <>
            {showOutcome && (
                <div>
                    <Label className="text-xs">Override outcome</Label>
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={v.outcome} onChange={(e) => setV({ ...v, outcome: e.target.value })} data-testid="decision-override-outcome">
                        <option value="approve">Approve as requested</option>
                        <option value="modify">Set a different outcome</option>
                        <option value="reject">Reject</option>
                    </select>
                </div>
            )}
            {needsChange && (
                <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-3 sm:col-span-1">
                        <Label className="text-xs">Final attendance</Label>
                        <select className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm bg-white" value={v.final_status} onChange={(e) => setV({ ...v, final_status: e.target.value })} data-testid="decision-final-status">
                            <option value="">From times only</option>
                            <option value="Present">Full day</option>
                            <option value="Half Day">Half day</option>
                            <option value="Absent">Absent (confirmed)</option>
                        </select>
                    </div>
                    <div><Label className="text-xs">Check-in (IST)</Label><Input type="time" value={v.ci} onChange={(e) => setV({ ...v, ci: e.target.value })} data-testid="decision-check-in" /></div>
                    <div><Label className="text-xs">Check-out (IST)</Label><Input type="time" value={v.co} onChange={(e) => setV({ ...v, co: e.target.value })} data-testid="decision-check-out" /></div>
                </div>
            )}
        </>
    );
}

export function DecisionDialog({ mode, request, onClose, onDone }) {
    const [v, setV] = useState({ remarks: "", final_status: "", ci: "", co: "", outcome: "approve" });
    const [busy, setBusy] = useState(false);
    useEffect(() => { setV({ remarks: "", final_status: "", ci: "", co: "", outcome: "approve" }); }, [mode]);
    if (!mode || !request) return null;
    const c = { ...COPY[mode], ...(isAutoAccepted(request) ? REVIEW_COPY[mode] : {}) };
    const Icon = c.icon;
    const date = request.attendance_date;

    const submit = async () => {
        if ((v.remarks || "").trim().length < c.min) { toast.error(`Reason must be at least ${c.min} characters.`); return; }
        const changes = { final_status: v.final_status || null, check_in_time: istToUtcIso(v.ci, date), check_out_time: istToUtcIso(v.co, date) };
        const changing = mode === "modify" || (mode === "override" && v.outcome === "modify");
        if (changing && !changes.final_status && !changes.check_in_time && !changes.check_out_time) { toast.error("Choose a final attendance or enter corrected times."); return; }
        const body = mode === "approve" ? { remarks: v.remarks || null }
            : mode === "reject" ? { remarks: v.remarks }
            : mode === "modify" ? { ...changes, remarks: v.remarks }
            : { outcome: v.outcome, ...(v.outcome === "modify" ? changes : {}), remarks: v.remarks };
        setBusy(true);
        try {
            await api.post(`/regularization/request/${request.id}/${mode}`, body);
            toast.success("Decision recorded.");
            onDone();
        } catch (err) {
            toast.error(formatApiError(err, "Decision failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="decision-dialog">
                <DialogHeader>
                    <DialogTitle>{c.title}</DialogTitle>
                    <DialogDescription>{c.desc}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {mode === "override" && request.month_finalised && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="decision-finalised-warning">
                            This month is finalised. Applying the override reopens it for this employee — finalise it again afterwards.
                        </div>
                    )}
                    {(mode === "modify" || mode === "override") && <OutcomeFields v={v} setV={setV} showOutcome={mode === "override"} />}
                    <div>
                        <Label className="text-xs">{c.min ? `Reason (required, at least ${c.min} characters)` : "Note (optional)"}</Label>
                        <Textarea rows={3} value={v.remarks} onChange={(e) => setV({ ...v, remarks: e.target.value })} data-testid="decision-remarks" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="decision-cancel-btn">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className={c.cls} data-testid="decision-confirm-btn"><Icon size={14} className="mr-1" /> {c.btn}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DecisionDialog;
